/**
 * CSV export utilities for BreakdownCell[] data
 * Works directly with flat cell structure instead of hierarchical EnhancedBreakdownData
 */

import type { BreakdownCell } from '@/components/common/breakdown-tables/types';
import { DimensionType } from '@/types/rebateAnalytics';

/**
 * Sanitizes a filename by removing special characters and converting to lowercase
 */
export function sanitizeFilename(name: string): string {
  return name
    .replace(/[^a-z0-9]/gi, '_')
    .replace(/_+/g, '_')
    .toLowerCase()
    .replace(/^_|_$/g, '');
}

/**
 * Formats a cell value for CSV export
 */
function formatCellValue(cell: BreakdownCell): string {
  const { value } = cell;
  if (value === null || value === undefined) {
    return '';
  }
  if (
    typeof value === 'object' &&
    'current' in value &&
    typeof (value as { current: number; previous: number; delta: number })
      .current === 'number'
  ) {
    // YOY object - use current value
    return (
      value as { current: number; previous: number; delta: number }
    ).current.toFixed(2);
  }
  if (typeof value === 'number') {
    return value.toFixed(2);
  }
  return String(value);
}

/**
 * Downloads a CSV file with the given content and filename
 */
function downloadCSVFile(content: string, filename: string): void {
  // Add UTF-8 BOM to ensure Excel properly detects encoding
  const bom = '\uFEFF';
  const blob = new Blob([bom + content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Build column header structure from cells
 */
function buildColumnHeaders(cells: BreakdownCell[]): string[] {
  const headers = new Set<string>(
    cells.map((cell) =>
      cell.subColumn ? `${cell.column} - ${cell.subColumn}` : cell.column,
    ),
  );

  // Sort headers chronologically
  const monthOrder = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  return Array.from(headers).sort((a, b) => {
    // Extract month from header (handle "Jan 2025 - Supplier A" format)
    const parseMonth = (header: string): { month: string; year?: number } => {
      const parts = header.split(' - ');
      const monthPart = parts[0].trim();
      const yearMatch = monthPart.match(/\d{4}/);
      const year = yearMatch ? parseInt(yearMatch[0], 10) : undefined;
      const month = yearMatch
        ? monthPart.replace(/\d{4}/, '').trim()
        : monthPart;
      return { month, year };
    };

    const aParsed = parseMonth(a);
    const bParsed = parseMonth(b);

    // Compare by year first
    if (aParsed.year !== bParsed.year) {
      return (aParsed.year || 0) - (bParsed.year || 0);
    }

    // Then by month
    const aMonthIdx = monthOrder.indexOf(aParsed.month);
    const bMonthIdx = monthOrder.indexOf(bParsed.month);
    if (aMonthIdx !== bMonthIdx) {
      return aMonthIdx - bMonthIdx;
    }

    // Then by subColumn if present
    const aSubCol = a.includes(' - ') ? a.split(' - ')[1] : '';
    const bSubCol = b.includes(' - ') ? b.split(' - ')[1] : '';
    return aSubCol.localeCompare(bSubCol);
  });
}

/**
 * Find a cell value by coordinates
 */
function findCellValue(
  cells: BreakdownCell[],
  column: string,
  subColumn: string | null,
  row: string,
  subRow: string | null,
): BreakdownCell | undefined {
  return cells.find(
    (cell) =>
      cell.column === column &&
      cell.subColumn === subColumn &&
      cell.row === row &&
      cell.subRow === subRow,
  );
}

/**
 * Get all unique rows (groups and sub-rows)
 */
function getUniqueRows(cells: BreakdownCell[]): Array<{
  row: string;
  subRow: string | null;
}> {
  // row → subRows mapping
  const rowMap = new Map<string, Set<string | null>>();

  cells.forEach((cell) => {
    const existing = rowMap.get(cell.row) ?? new Set<string | null>();
    existing.add(cell.subRow ?? null);
    rowMap.set(cell.row, existing);
  });

  const sortedByRow = Array.from(rowMap.entries()).toSorted(([a], [b]) => {
    return a.localeCompare(b);
  });

  return sortedByRow.flatMap(([row, subRows]) => {
    return Array.from(subRows)
      .sort((a, b) => {
        if (a === b) return 0;
        if (a === null) return -1;
        if (b === null) return 1;
        return a.localeCompare(b);
      })
      .map((subRow) => {
        return { row, subRow };
      });
  });
}

/**
 * Generates CSV content from BreakdownCell[] for full table export
 */
export function generateTableCSVFromCells({
  cells,
  groupLabel,
  subRowLabel,
}: {
  cells: BreakdownCell[];
  groupLabel: string;
  subRowLabel?: string;
  tableName: string;
}): string {
  const csvRows: string[][] = [];
  const headers = buildColumnHeaders(cells);
  const rows = getUniqueRows(cells);

  // Check if there are any subRows
  const hasSubRows = rows.some((rowData) => rowData.subRow !== null);

  const headerRow = hasSubRows
    ? [groupLabel || 'Group', subRowLabel || '', ...headers]
    : [groupLabel || 'Group', ...headers];
  csvRows.push(headerRow);

  // Calculate summary totals
  const summaryTotals = headers.map((header) => {
    const parts = header.split(' - ');
    const column = parts[0];
    const subColumn = parts.length > 1 ? parts[1] : null;

    return rows.reduce((sum, rowData) => {
      // Only count group-level rows (subRow = null) for totals
      if (rowData.subRow === null) {
        const cell = findCellValue(cells, column, subColumn, rowData.row, null);
        if (cell) {
          const value = cell.value;
          if (typeof value === 'number') {
            return sum + value;
          }
          if (
            value !== null &&
            typeof value === 'object' &&
            'current' in value &&
            typeof (
              value as { current: number; previous: number; delta: number }
            ).current === 'number'
          ) {
            return (
              sum +
              (value as { current: number; previous: number; delta: number })
                .current
            );
          }
        }
      }
      return sum;
    }, 0);
  });

  rows.forEach((rowData) => {
    const rowCells = hasSubRows
      ? [rowData.row, rowData.subRow ?? '']
      : [rowData.row];

    const row = [
      ...rowCells,
      ...headers.map((header) => {
        const parts = header.split(' - ');
        const column = parts[0];
        const subColumn = parts.length > 1 ? parts[1] : null;

        const cell = findCellValue(
          cells,
          column,
          subColumn,
          rowData.row,
          rowData.subRow,
        );

        return cell ? formatCellValue(cell) : '';
      }),
    ];
    csvRows.push(row);
  });

  // Add summary row
  const summaryRowStart = hasSubRows
    ? ['Total', 'All groups combined']
    : ['Total'];
  const summaryRow = [
    ...summaryRowStart,
    ...summaryTotals.map((total) => total.toFixed(2)),
  ];
  csvRows.push(summaryRow);

  return csvRows
    .map((row) => row.map((cell) => `"${cell}"`).join(','))
    .join('\n');
}

/**
 * Downloads the full breakdown table as CSV
 */
export function downloadTableCSVFromCells({
  cells,
  groupLabel,
  subRowLabel,
  tableName,
}: {
  cells: BreakdownCell[];
  groupLabel: string;
  subRowLabel?: string;
  tableName: string;
}): void {
  const csvContent = generateTableCSVFromCells({
    cells,
    groupLabel,
    subRowLabel,
    tableName,
  });
  const filename = `${sanitizeFilename(tableName)}_breakdown.csv`;
  downloadCSVFile(csvContent, filename);
}

/**
 * Generates CSV content for a single group (by group)
 */
function generateGroupCSVFromCells({
  cells,
  groupRow,
  groupLabel,
  subRowLabel,
}: {
  cells: BreakdownCell[];
  groupRow: string;
  groupLabel: string;
  subRowLabel?: string;
}): string {
  const csvRows: string[][] = [];
  const headers = buildColumnHeaders(cells);

  // Filter cells for this group
  const groupCells = cells.filter((cell) => cell.row === groupRow);

  if (groupCells.length === 0) {
    return ''; // No cells for this group
  }

  // Check if there are sub-rows (subRow !== null and !== undefined)
  const hasSubRows = groupCells.some(
    (cell) => cell.subRow !== null && cell.subRow !== undefined,
  );

  if (hasSubRows) {
    // Case 1: Has sub-rows - generate CSV with sub-rows only
    const subRowCells = groupCells.filter(
      (cell) => cell.subRow !== null && cell.subRow !== undefined,
    );
    const subRows = Array.from(
      new Set(subRowCells.map((cell) => cell.subRow).filter(Boolean)),
    ).sort();

    csvRows.push([subRowLabel || groupLabel || 'Item', ...headers]);

    // Sub-rows only (skip group-level row since group name is in filename)
    subRows.forEach((subRow) => {
      const subRowData = [
        subRow || '',
        ...headers.map((header) => {
          const parts = header.split(' - ');
          const column = parts[0];
          const subColumn = parts.length > 1 ? parts[1] : null;

          const cell = findCellValue(
            cells,
            column,
            subColumn,
            groupRow,
            subRow ?? null,
          );
          return cell ? formatCellValue(cell) : '';
        }),
      ];
      csvRows.push(subRowData);
    });
  } else {
    // Case 2: No sub-rows - generate CSV with just the group-level row
    csvRows.push([groupLabel || 'Item', ...headers]);

    // Group-level row data
    const rowData = [
      groupRow,
      ...headers.map((header) => {
        const parts = header.split(' - ');
        const column = parts[0];
        const subColumn = parts.length > 1 ? parts[1] : null;

        const cell = findCellValue(cells, column, subColumn, groupRow, null);
        return cell ? formatCellValue(cell) : '';
      }),
    ];
    csvRows.push(rowData);
  }

  return csvRows
    .map((row) => row.map((cell) => `"${cell}"`).join(','))
    .join('\n');
}

/**
 * Downloads CSV files for each group separately
 */
export function downloadMultipleGroupCSVsFromCells({
  cells,
  groupLabel,
  subRowLabel,
  tableName,
}: {
  cells: BreakdownCell[];
  groupLabel: string;
  subRowLabel?: string;
  tableName: string;
}): void {
  // Get unique group rows (subRow = null or undefined)
  // Handle both null and undefined since BreakdownCell allows subRow?: string | null
  const groupRows = Array.from(new Set(cells.map((c) => c.row)));

  groupRows.forEach((groupRow) => {
    const csvContent = generateGroupCSVFromCells({
      cells,
      groupRow,
      groupLabel,
      subRowLabel,
    });

    if (!csvContent) return; // Skip if no sub-rows

    const groupFileName = sanitizeFilename(groupRow);
    const tableFileName = sanitizeFilename(tableName);
    const filename = `${tableFileName}_${groupFileName}.csv`;

    // Use setTimeout to ensure all downloads work properly
    setTimeout(() => {
      downloadCSVFile(csvContent, filename);
    }, 100);
  });
}

/**
 * Downloads CSV files organized by secondary X dimension
 */
export function downloadBySecondaryXAxisCSVFromCells({
  cells,
  groupLabel,
  subRowLabel,
  tableName,
}: {
  cells: BreakdownCell[];
  secondaryXDimension: DimensionType;
  groupLabel: string;
  subRowLabel?: string;
  tableName: string;
}): void {
  // Extract unique secondary X dimension values (subColumn values)
  const secondaryXValues = new Set<string>();
  cells.forEach((cell) => {
    if (cell.subColumn) {
      secondaryXValues.add(cell.subColumn);
    }
  });

  // Extract unique months (primary columns)
  const months = new Set<string>();
  cells.forEach((cell) => {
    months.add(cell.column);
  });

  // Sort months chronologically
  const monthOrder = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const sortedMonths = Array.from(months).sort((a, b) => {
    // Extract month from column (handle "Jan 2025" format)
    const parseMonth = (col: string): { month: string; year?: number } => {
      const yearMatch = col.match(/\d{4}/);
      const year = yearMatch ? parseInt(yearMatch[0], 10) : undefined;
      const month = yearMatch ? col.replace(/\d{4}/, '').trim() : col;
      return { month, year };
    };

    const aParsed = parseMonth(a);
    const bParsed = parseMonth(b);

    if (aParsed.year !== bParsed.year) {
      return (aParsed.year || 0) - (bParsed.year || 0);
    }

    const aMonthIdx = monthOrder.indexOf(aParsed.month);
    const bMonthIdx = monthOrder.indexOf(bParsed.month);
    return aMonthIdx - bMonthIdx;
  });

  // Get unique rows once for reuse
  const rows = getUniqueRows(cells);
  const hasSubRows = rows.some((rowData) => rowData.subRow !== null);

  // Generate a CSV file for each secondary X dimension value
  Array.from(secondaryXValues).forEach((secondaryXValue) => {
    const csvRows: string[][] = [];

    // Header row: Y dimension labels + months
    const headerRow = hasSubRows
      ? [groupLabel || 'Group', subRowLabel || '', ...sortedMonths]
      : [groupLabel || 'Group', ...sortedMonths];
    csvRows.push(headerRow);

    rows.forEach((rowData) => {
      const rowCells = hasSubRows
        ? [rowData.row, rowData.subRow ?? '']
        : [rowData.row];

      const row = [
        ...rowCells,
        ...sortedMonths.map((month) => {
          // Find cell for this month and secondary X value
          const cell = findCellValue(
            cells,
            month,
            secondaryXValue,
            rowData.row,
            rowData.subRow,
          );
          return cell ? formatCellValue(cell) : '';
        }),
      ];
      csvRows.push(row);
    });

    // Add summary row
    const summaryTotals = sortedMonths.map((month) => {
      return rows
        .filter((r) => r.subRow === null) // Only group-level rows
        .reduce((sum, rowData) => {
          const cell = findCellValue(
            cells,
            month,
            secondaryXValue,
            rowData.row,
            null,
          );
          if (cell) {
            const value = cell.value;
            if (value === null) {
              return sum;
            }
            if (typeof value === 'number') {
              return sum + value;
            }
            if (
              value !== null &&
              typeof value === 'object' &&
              'current' in value &&
              typeof (
                value as { current: number; previous: number; delta: number }
              ).current === 'number'
            ) {
              return (
                sum +
                (value as { current: number; previous: number; delta: number })
                  .current
              );
            }
          }
          return sum;
        }, 0);
    });

    const summaryRowStart = hasSubRows
      ? ['Total', 'All groups combined']
      : ['Total'];
    csvRows.push([
      ...summaryRowStart,
      ...summaryTotals.map((t) => t.toFixed(2)),
    ]);

    const csvContent = csvRows
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n');

    const secondaryXFileName = sanitizeFilename(secondaryXValue);
    const tableFileName = sanitizeFilename(tableName);
    const filename = `${tableFileName}_${secondaryXFileName}.csv`;

    // Use setTimeout to ensure all downloads work properly
    setTimeout(() => {
      downloadCSVFile(csvContent, filename);
    }, 100);
  });
}
